import { gql, useMutation } from '@apollo/client';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { FormikErrors, useFormik } from 'formik';
import React from 'react';
import * as Yup from 'yup';

import { Form } from 'src/components/common/form/Form';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { FormikBaseTestProps } from 'src/lib/forms/FormikTestHelpers';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { ChannelInput } from '../addNotificationWizard/ChannelInput';
import { integrationKeyRegex } from '../addNotificationWizard/createPagerDutyChannel/CreatePagerDutyChannel';
import { slackWebhookRegex } from '../addNotificationWizard/createSlackChannelModal/CreateSlackChannelModal';
import { PagerDutyRoutingKeyToolTip } from '../addNotificationWizard/pagerDutyRoutingKeyToolTip/PagerDutyRoutingKeyToolTip';

export const errorMessages = {
  slackURL: {
    required: 'Please provide a Slack URL.',
    matches: 'Please provide a valid Slack URL.',
  },
  routingKey: {
    required: 'Please provide a routing key for PagerDuty.',
    matches: 'Please provide a valid routing key.',
  },
  webhookURL: {
    required: 'Please provide a webhook URL.',
    valid: 'Please provide a valid webhook URL.',
  },
};

export const editChannelMutation = gql`
  mutation EditChannelModalUpsertChannelMutation(
    $graphId: ID!
    $channelId: ID!
    $slackChannel: SlackChannelInput
    $pagerDutyChannel: PagerDutyChannelInput
    $webhookChannel: WebhookChannelInput
  ) {
    service(id: $graphId) {
      upsertChannel(
        id: $channelId
        slackChannel: $slackChannel
        pagerDutyChannel: $pagerDutyChannel
        webhookChannel: $webhookChannel
      ) {
        id
      }
    }
  }
`;

/**
 * Schema for the form
 */
const slackValidationSchema = Yup.object().shape({
  slackURL: Yup.string()
    .required(errorMessages.slackURL.required)
    .matches(slackWebhookRegex, errorMessages.slackURL.matches),
});

const pagerDutyValidationSchema = Yup.object().shape({
  routingKey: Yup.string()
    .required(errorMessages.routingKey.required)
    .matches(integrationKeyRegex, errorMessages.routingKey.matches),
});

const webhookValidationSchema = Yup.object().shape({
  webhookURL: Yup.string()
    .required(errorMessages.webhookURL.required)
    .url(errorMessages.webhookURL.valid),
});

const map = {
  SlackChannel: slackValidationSchema,
  PagerDutyChannel: pagerDutyValidationSchema,
  WebhookChannel: webhookValidationSchema,
};

type SlackFormValues = Yup.InferType<typeof slackValidationSchema>;
type PagerDutyFormValues = Yup.InferType<typeof pagerDutyValidationSchema>;
type WebhookFormValues = Yup.InferType<typeof webhookValidationSchema>;
/**
 * Types inferred from `validationSchema`
 */
type FormValues = SlackFormValues | PagerDutyFormValues | WebhookFormValues;

export const editChannelModalChannelFragment = gql`
  fragment EditChannelModalChannelFragment on Channel {
    id
    __typename
    ... on SlackChannel {
      url
    }
    ... on WebhookChannel {
      url
    }
    ... on PagerDutyChannel {
      routingKey
    }
  }
`;

interface Props {
  isOpen: boolean;
  graphId: string;
  channel: GraphQLTypes.EditChannelModalChannelFragment;
  onClose: () => void;
  __test?: FormikBaseTestProps<FormValues> & {
    error?: Error;
  };
}

export function EditChannelModal({
  isOpen,
  onClose,
  graphId,
  channel,
  __test = {},
}: Props) {
  const [editChannel, { error = __test.error }] = useMutation<
    GraphQLTypes.EditChannelModalUpsertChannelMutation,
    GraphQLTypes.EditChannelModalUpsertChannelMutationVariables
  >(editChannelMutation);
  const { errors, values, handleChange, handleSubmit, isSubmitting } =
    useFormik<FormValues>({
      enableReinitialize: true,
      initialValues: {
        slackURL: channel.__typename === 'SlackChannel' ? channel.url : '',
        routingKey:
          channel.__typename === 'PagerDutyChannel' ? channel.routingKey : '',
        webhookURL: channel.__typename === 'WebhookChannel' ? channel.url : '',
      },
      validationSchema: map[channel.__typename],
      onSubmit: async (formValues) => {
        try {
          if (channel.__typename === 'WebhookChannel') {
            await editChannel({
              variables: {
                graphId,
                channelId: channel.id,
                webhookChannel: {
                  url: webhookValidationSchema.cast(formValues).webhookURL,
                },
              },
            });
            onClose();
          } else if (channel.__typename === 'PagerDutyChannel') {
            await editChannel({
              variables: {
                graphId,
                channelId: channel.id,
                pagerDutyChannel: {
                  routingKey:
                    pagerDutyValidationSchema.cast(formValues).routingKey,
                },
              },
            });
            onClose();
          } else if (channel.__typename === 'SlackChannel') {
            await editChannel({
              variables: {
                graphId,
                channelId: channel.id,
                slackChannel: {
                  url: slackValidationSchema.cast(formValues).slackURL,
                },
              },
            });
            onClose();
          }
        } catch {
          // We don't need to do anything to handle this here since the error will
          // come back in the `error` from our useMutation hook
        }
      },
    });

  return (
    <Modal isOpen={isOpen} size="xl" onClose={onClose}>
      <ModalOverlay />
      <Form onSubmit={handleSubmit} noValidate>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Edit channel</ModalTitle>
            <ModalDescription>
              Please provide the following information.
            </ModalDescription>
          </ModalHeader>
          <ModalBody>
            {channel.__typename !== 'PagerDutyChannel' ? (
              <ChannelInput
                inputName={
                  channel.__typename === 'SlackChannel'
                    ? 'slackURL'
                    : 'webhookURL'
                }
                autoFocus={true}
                isDisabled={isSubmitting}
                error={
                  channel.__typename === 'SlackChannel'
                    ? (errors as FormikErrors<SlackFormValues>).slackURL
                    : (errors as FormikErrors<WebhookFormValues>).webhookURL
                }
                handleChange={handleChange}
                value={
                  channel.__typename === 'SlackChannel'
                    ? slackValidationSchema.cast(values).slackURL
                    : webhookValidationSchema.cast(values).webhookURL
                }
                label={
                  <div className="flex items-center">
                    URL
                    <Tooltip
                      label={
                        channel.__typename === 'SlackChannel'
                          ? 'The Incoming Webhook URL provided to you by Slack'
                          : 'The URL that should receive a POST request'
                      }
                    >
                      <IconInfo className="ml-2 cursor-pointer text-link" />
                    </Tooltip>
                  </div>
                }
                isInvalid={
                  channel.__typename === 'SlackChannel'
                    ? !!(errors as FormikErrors<SlackFormValues>).slackURL
                    : !!(errors as FormikErrors<WebhookFormValues>).webhookURL
                }
              />
            ) : (
              <ChannelInput
                inputName="routingKey"
                autoFocus={true}
                isDisabled={isSubmitting}
                error={(errors as FormikErrors<PagerDutyFormValues>).routingKey}
                handleChange={handleChange}
                value={pagerDutyValidationSchema.cast(values).routingKey}
                label={
                  <div className="flex items-center">
                    Routing Key
                    <PagerDutyRoutingKeyToolTip />
                  </div>
                }
                isInvalid={
                  !!(errors as FormikErrors<PagerDutyFormValues>).routingKey
                }
              />
            )}

            {error && <ErrorMessage>{error.message}</ErrorMessage>}
          </ModalBody>
          <ModalFooter>
            <ButtonGroup>
              <Button variant="secondary" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                isLoading={isSubmitting}
                loadingText="Save"
                type="submit"
              >
                Save
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  );
}
